import React, { useState, useMemo, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Save, Copy, CheckCircle2, Wrench, Info, Layers, Sliders, PlaneTakeoff, PlaneLanding } from "lucide-react";
import { BCM_STYLE as S } from "../styles/BookingCalendarStyle";
import InstrumentLoader from "./InstrumentLoader";
import { createPortal } from "react-dom";
import { ExperienceDetailsDto, TimeSlotDto, TimePointDto } from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (experienceDetails: ExperienceDetailsDto) => void;
  registration: string;
  initialExperienceDetails?: ExperienceDetailsDto;
}

type SlotStateMap = Record<number, 'FREE_FOR_RENT' | 'BLOCKED_BY_USER'>;

const ExperienceCalendarModal = ({ isOpen, onClose, onSave, registration, initialExperienceDetails }: Props) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(true);
  const [dailySlots, setDailySlots] = useState<Record<string, SlotStateMap>>({});
  const [activeMarkingType, setActiveMarkingType] = useState<'FREE_FOR_RENT' | 'BLOCKED_BY_USER'>('FREE_FOR_RENT');
  const [localLoading, setLocalLoading] = useState(false);
  const [slotToUuidMap, setSlotToUuidMap] = useState<Record<string, Record<number, string>>>({});

  const [intervalStep, setIntervalStep] = useState<number>(initialExperienceDetails?.durationInMinutes || 60);
  const [startHour, setStartHour] = useState<number>(6);
  const [endHour, setEndHour] = useState<number>(22);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const durationOptions = [
    { label: "30 Min", value: 30 },
    { label: "1 Oră", value: 60 },
    { label: "1.5 Ore", value: 90 },
    { label: "2 Ore", value: 120 },
    { label: "2.5 Ore", value: 150 },
    { label: "3 Ore", value: 180 },
    { label: "3.5 Ore", value: 210 },
    { label: "4 Ore", value: 240 },
    { label: "4.5 Ore", value: 270 },
    { label: "5 Ore", value: 300 }
  ];

  const hourOptions = useMemo(() => {
    return Array.from({ length: 25 }, (_, i) => ({
      label: `${i.toString().padStart(2, '0')}:00`,
      value: i
    }));
  }, []);

  useEffect(() => {
    if (isOpen) {
      const step = initialExperienceDetails?.durationInMinutes || intervalStep;
      setIntervalStep(step);

      if (initialExperienceDetails?.timeInterval) {
        const parts = initialExperienceDetails.timeInterval.split(" - ");
        if (parts.length === 2) {
          const sH = parseInt(parts[0].split(":")[0], 10);
          const eH = parseInt(parts[1].split(":")[0], 10);
          if (!isNaN(sH)) setStartHour(sH);
          if (!isNaN(eH)) setEndHour(eH);
        }
      }

      const timeSlotsList = initialExperienceDetails?.calendarPayloadDto?.timeSlotDtoList;
      if (timeSlotsList && timeSlotsList.length > 0) {
        const hydratedSlots: Record<string, SlotStateMap> = {};
        const hydratedUuidMap: Record<string, Record<number, string>> = {};
        const hydratedDates = new Set<string>();

        timeSlotsList.forEach((slotDto) => {
          if (!slotDto.from || !slotDto.to) return;

          const fromYear = slotDto.from.year ?? today.getFullYear();
          const fromMonth = (slotDto.from.month ?? 1) - 1;
          const fromDay = slotDto.from.day ?? 1;
          const fromHour = slotDto.from.hour ?? 0;
          const fromMinute = slotDto.from.minute ?? 0;

          const startDate = new Date(fromYear, fromMonth, fromDay, 0, 0, 0, 0);
          const dateKey = startDate.toDateString();
          hydratedDates.add(dateKey);

          if (!hydratedSlots[dateKey]) hydratedSlots[dateKey] = {};
          if (!hydratedUuidMap[dateKey]) hydratedUuidMap[dateKey] = {};

          const startTotalMin = fromHour * 60 + fromMinute;

          const slotTypeEnumMap: Record<number, 'FREE_FOR_RENT' | 'BLOCKED_BY_USER'> = {
            1: 'FREE_FOR_RENT',
            2: 'BLOCKED_BY_USER'
          };
          const mappedState = slotDto.timeSlotType !== undefined ? slotTypeEnumMap[slotDto.timeSlotType] || 'FREE_FOR_RENT' : 'FREE_FOR_RENT';

          hydratedSlots[dateKey][startTotalMin] = mappedState;
          if (slotDto.uuid) {
            hydratedUuidMap[dateKey][startTotalMin] = slotDto.uuid;
          }
        });

        setDailySlots(hydratedSlots);
        setSlotToUuidMap(hydratedUuidMap);
        setSelectedDates(Array.from(hydratedDates));

        if (hydratedDates.size > 0) {
          const dateArray = Array.from(hydratedDates);
          const firstFuture = dateArray.find(d => new Date(d) >= today) || dateArray[0];
          setActiveDate(firstFuture);
        }
      } else {
        // 🟢 La prima deschidere, calendarul pornește curat, cu luna curentă selectată, dar FĂRĂ sloturi pre-bifate automat (lăsând utilizatorul să-și pună orele dorite)
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();

        const defaultMonthDates: string[] = [];

        for (let i = 1; i <= daysInCurrentMonth; i++) {
          const dayObj = new Date(year, month, i);
          dayObj.setHours(0, 0, 0, 0);

          if (dayObj >= today) {
            defaultMonthDates.push(dayObj.toDateString());
          }
        }

        setDailySlots({});
        setSelectedDates(defaultMonthDates);
        setSlotToUuidMap({});
        setIsMultiSelectMode(true);

        if (defaultMonthDates.length > 0) {
          setActiveDate(defaultMonthDates[0]);
        }
      }
    }
  }, [isOpen]);

  // Generarea corectă a sloturilor în interfață folosind strict intervalStep (fără suprapuneri)
  const timeSlots = useMemo(() => {
    const slots = [];
    const startMin = startHour * 60;
    const endMin = endHour * 60;

    for (let totalMin = startMin; totalMin + intervalStep <= endMin; totalMin += intervalStep) {
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      slots.push({ id: totalMin, label: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`, hour: h });
    }
    return slots;
  }, [intervalStep, startHour, endHour]);

  const toggleSlot = (slotId: number) => {
    if (!activeDate) return;

    setDailySlots(prev => {
      const dayMap = { ...(prev[activeDate] || {}) };
      if (dayMap[slotId]) {
        delete dayMap[slotId];
      } else {
        dayMap[slotId] = activeMarkingType;
      }
      return { ...prev, [activeDate]: dayMap };
    });
  };

  const handleManualSelectMode = () => {
    setSelectedDates([]);
    setDailySlots({});
    setActiveDate(null);
    setIsMultiSelectMode(true);
  };

  const handleBulkSelect = (filterFn: (d: Date) => boolean) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const newDatesInMonth: string[] = [];
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      if (filterFn(date) && date >= today) {
        newDatesInMonth.push(date.toDateString());
      }
    }

    setIsMultiSelectMode(true);
    setSelectedDates(prev => Array.from(new Set([...prev, ...newDatesInMonth])));
    if (newDatesInMonth.length > 0 && !activeDate) setActiveDate(newDatesInMonth[0]);
  };

  const handleDateClick = (date: Date) => {
    if (date < today) return;
    const dStr = date.toDateString();
    if (isMultiSelectMode) {
      const isAlreadySelected = selectedDates.includes(dStr);
      setSelectedDates(isAlreadySelected ? selectedDates.filter(d => d !== dStr) : [...selectedDates, dStr]);
      setActiveDate(dStr);
    } else {
      setSelectedDates([dStr]);
      setActiveDate(dStr);
    }
  };

  const applyToAllSelected = () => {
    if (!activeDate || selectedDates.length < 2) return;
    const sourceConfig = dailySlots[activeDate] || {};
    setDailySlots(prev => {
      const newDailySlots = { ...prev };
      selectedDates.forEach(dateStr => { newDailySlots[dateStr] = { ...sourceConfig }; });
      return newDailySlots;
    });
    message.success(`S-a sincronizat configurarea pe ${selectedDates.length} zile.`);
  };

  const handleSave = () => {
    const timeSlotDtoList: TimeSlotDto[] = [];

    Object.entries(dailySlots).forEach(([dateStr, slots]) => {
      const dayDate = new Date(dateStr);
      const year = dayDate.getFullYear();
      const month = dayDate.getMonth() + 1;
      const day = dayDate.getDate();

      // 🟢 Salvăm strict doar căsuțele bifate de utilizator, calculând corect capătul pe baza duratei (intervalStep)
      Object.entries(slots).forEach(([idStr, targetState]) => {
        const startMin = parseInt(idStr, 10);
        const endMin = startMin + intervalStep;

        const startH = Math.floor(startMin / 60);
        const startMinute = startMin % 60;

        const endH = Math.floor(endMin / 60);
        const endMinute = endMin % 60;

        const timeSlotTypeEnumMap: Record<string, number> = {
          'FREE_FOR_RENT': 1,
          'BLOCKED_BY_USER': 2
        };

        const fromPoint: TimePointDto = {
          timePointType: 1,
          year,
          month,
          day,
          hour: startH,
          minute: startMinute
        };

        const toPoint: TimePointDto = {
          timePointType: 2,
          year,
          month,
          day,
          hour: endH,
          minute: endMinute
        };

        const existingUuid = slotToUuidMap[dateStr]?.[startMin];

        timeSlotDtoList.push({
          uuid: existingUuid || (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
          bookingId: 0,
          from: fromPoint,
          to: toPoint,
          timeSlotType: timeSlotTypeEnumMap[targetState] || 1
        });
      });
    });

    const payload: ExperienceDetailsDto = {
      ...initialExperienceDetails,
      durationInMinutes: intervalStep,
      timeInterval: `${startHour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:00`,
      calendarPayloadDto: {
        timeSlotDtoList
      }
    };

    onSave(payload);
  };

  if (!isOpen) return null;

  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();
  const monthNames = ["IAN", "FEB", "MAR", "APR", "MAI", "IUN", "IUL", "AUG", "SEP", "OCT", "NOI", "DEC"];
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const adjustedFirstDay = new Date(currentYear, currentMonth, 1).getDay() === 0 ? 6 : new Date(currentYear, currentMonth, 1).getDay() - 1;

  return createPortal(
    <div className={`${S.overlay2} overflow-hidden max-h-screen fixed inset-0`}>
      <InstrumentLoader visible={localLoading} />
      <div className={`${S.modal2} max-h-[90vh] overflow-hidden flex`}>
        <div className={`${S.sidebarLeft} flex flex-col h-full overflow-y-auto no-scrollbar shrink-0`}>
          <div className="flex items-center justify-between mb-4">
            <div className={S.stepHeader}>
              <div className={S.stepIconBlue}><CalendarIcon size={18} /></div>
              <div>
                <p className={S.stepLabel}>Pasul 01</p>
                <h3 className={S.stepTitle}>Alege Ziua</h3>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsMultiSelectMode(!isMultiSelectMode);
                if (isMultiSelectMode) setSelectedDates(activeDate ? [activeDate] : []);
              }}
              className={`p-2 rounded-xl transition-all border ${isMultiSelectMode ? 'bg-orange-500 border-orange-400 text-white shadow-[0_0_15px_rgba(242,108,21,0.4)]' : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:text-slate-300'}`}
            >
              <Layers size={16} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <button type="button" onClick={() => handleBulkSelect(() => true)} className="py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-[9px] font-black uppercase text-slate-400 hover:text-white transition-all">+ Toată luna</button>
            <button type="button" onClick={() => handleBulkSelect((d) => d.getDay() !== 0 && d.getDay() !== 6)} className="py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-[9px] font-black uppercase text-slate-400 hover:text-white transition-all">+ Luni-Vineri</button>
            <button
              type="button"
              onClick={handleManualSelectMode}
              className="col-span-2 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-[9px] font-black uppercase text-slate-400 hover:text-white hover:border-orange-500/40 transition-all"
            >
              Selectare manuală
            </button>
          </div>

          <div className={S.calendarNav}>
            <button type="button" onClick={() => setViewDate(new Date(currentYear, currentMonth - 1, 1))} className={S.calendarNavBtn} disabled={currentYear <= today.getFullYear() && currentMonth <= today.getMonth()}><ChevronLeft size={16}/></button>
            <span className={S.monthDisplay}>{monthNames[currentMonth]} {currentYear}</span>
            <button type="button" onClick={() => setViewDate(new Date(currentYear, currentMonth + 1, 1))} className={S.calendarNavBtn}><ChevronRight size={16}/></button>
          </div>

          <div className={S.calendarGrid}>
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(h => <div key={h} className={S.weekdayLabel}>{h}</div>)}
            {Array.from({ length: adjustedFirstDay }).map((_, idx) => <div key={`spacer-${idx}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const dateObj = new Date(currentYear, currentMonth, i + 1);
              const dStr = dateObj.toDateString();
              const isSelected = selectedDates.includes(dStr);
              const isActive = activeDate === dStr;
              const hasConfig = dailySlots[dStr] && Object.keys(dailySlots[dStr]).length > 0;
              const isPast = dateObj < today;

              return (
                <button
                  type="button"
                  key={i}
                  disabled={isPast}
                  onClick={() => handleDateClick(dateObj)}
                  className={`relative ${S.dayButtonBase} ${isPast ? 'opacity-20 grayscale cursor-not-allowed' : ''} ${(isSelected && isMultiSelectMode) || (isActive && !isMultiSelectMode) ? S.dayButtonActive : S.dayButtonInactive} ${isActive && isMultiSelectMode ? 'ring-1 ring-orange-500' : ''}`}
                >
                  {i + 1}
                  {hasConfig && <span className={`absolute bottom-1 w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-orange-500'}`}></span>}
                </button>
              );
            })}
          </div>

          <div className="mt-auto pt-4">
            {isMultiSelectMode && selectedDates.length > 1 && (
              <button
                type="button"
                onClick={applyToAllSelected}
                className="w-full px-3 py-3 bg-orange-600/10 border border-orange-500/30 text-orange-500 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-orange-600 hover:text-white transition-all flex items-center justify-center gap-2 text-center"
              >
                <Copy size={14} className="shrink-0" />
                <span className="truncate">Sincronizează ({selectedDates.length} zile)</span>
              </button>
            )}
          </div>
        </div>

        <div className={`${S.mainContent} flex-1 flex flex-col h-full overflow-hidden relative`}>
          <button type="button" onClick={onClose} className="absolute top-6 right-8 text-slate-500 hover:text-white transition-colors z-20"><X size={20}/></button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-3 shrink-0">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className={S.clockGlow}></div>
                <div className={S.clockIconBox}><Clock size={18} /></div>
              </div>
              <div>
                <p className={S.timelineHeaderLabel}>{registration}</p>
                <h3 className={S.timelineHeaderTitle}>{activeDate || 'Selectează o dată din calendar'}</h3>
              </div>
            </div>

            <div className="flex gap-1.5 bg-slate-900/80 p-1 rounded-xl border border-white/5 backdrop-blur-md">
              <button type="button" onClick={() => setActiveMarkingType('FREE_FOR_RENT')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${activeMarkingType === 'FREE_FOR_RENT' ? 'bg-orange-500 text-white' : 'text-slate-500 hover:text-slate-400'}`}>
                <CheckCircle2 size={12} /> Liber de închiriat
              </button>
              <button type="button" onClick={() => setActiveMarkingType('BLOCKED_BY_USER')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${activeMarkingType === 'BLOCKED_BY_USER' ? 'bg-red-600 text-white' : 'text-slate-500 hover:text-slate-400'}`}>
                <Wrench size={12} /> Blocat
              </button>
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden space-y-3">
            {!activeDate ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-700">
                <CalendarIcon size={48} strokeWidth={1} className="mb-4 opacity-20" />
                <p className="text-[10px] font-black uppercase tracking-[0.3em]">Așteptare selecție zbor secundar</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-3 p-4 bg-slate-900/40 rounded-2xl border border-white/5 shrink-0 animate-in fade-in duration-300">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                      <Sliders size={12} className="text-blue-400" /> Pasul 02 // Durată Activitate (Slot)
                    </div>
                    <div className="flex flex-row gap-1.5 overflow-x-auto no-scrollbar pb-1 whitespace-nowrap w-full">
                      {durationOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setIntervalStep(opt.value)}
                          className={`px-4 py-2 rounded-xl font-mono text-[10px] font-bold transition-all border inline-block ${
                            intervalStep === opt.value
                              ? "bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/10"
                              : "bg-slate-950/60 border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-950"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5 pt-2 border-t border-white/5">
                    <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                      <Clock size={12} className="text-emerald-400" /> Pasul 03 // Fereastră de Lucru Operabilă
                    </div>
                    <div className="flex items-center gap-3 bg-slate-950/50 p-2 rounded-xl border border-slate-800/80 max-w-md">
                      <div className="flex-1 flex items-center gap-2">
                        <PlaneTakeoff size={12} className="text-slate-500" />
                        <select
                          value={startHour}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (val < endHour) setStartHour(val);
                          }}
                          className="w-full bg-transparent font-mono text-xs font-bold text-white outline-none cursor-pointer appearance-none"
                        >
                          {hourOptions.map(h => (
                            <option key={`start-${h.value}`} value={h.value} className="bg-[#0F172A] text-white">{h.label}</option>
                          ))}
                        </select>
                      </div>
                      <span className="text-slate-600 font-mono text-xs">—</span>
                      <div className="flex-1 flex items-center gap-2">
                        <PlaneLanding size={12} className="text-slate-500" />
                        <select
                          value={endHour}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (val > startHour) setEndHour(val);
                          }}
                          className="w-full bg-transparent font-mono text-xs font-bold text-white outline-none cursor-pointer appearance-none"
                        >
                          {hourOptions.map(h => (
                            <option key={`end-${h.value}`} value={h.value} className="bg-[#0F172A] text-white">{h.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-1 no-scrollbar min-h-0 animate-in fade-in duration-300">
                  <div className="relative pt-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-slate-950/60 px-3 py-1 rounded-md border border-white/5 flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-orange-500" /> Pasul 04 // Mapează Sloturi Active ({startHour.toString().padStart(2, '0')}:00 - {endHour.toString().padStart(2, '0')}:00)
                      </span>
                      <div className={S.rowDivider}></div>
                    </div>

                    <div className={S.slotsGrid}>
                      {timeSlots.map((slot) => {
                        const type = dailySlots[activeDate]?.[slot.id];
                        let stateKey: keyof typeof S.slotStates = 'default';

                        if (type === 'FREE_FOR_RENT') stateKey = 'selected';
                        else if (type === 'BLOCKED_BY_USER') stateKey = 'maintenance';

                        return (
                          <button
                            type="button"
                            key={slot.id}
                            onClick={() => toggleSlot(slot.id)}
                            className={`${S.slotBase} ${S.slotStates[stateKey]} h-14 active:scale-95 transition-all`}
                          >
                            <span className={`text-[10px] font-mono font-bold ${type ? 'text-white' : 'text-slate-400'}`}>{slot.label}</span>
                            <span className={`text-[7px] font-black uppercase tracking-widest mt-0.5 ${type === 'FREE_FOR_RENT' ? 'text-orange-400' : type === 'BLOCKED_BY_USER' ? 'text-red-500' : 'text-slate-600'}`}>
                              {type === 'FREE_FOR_RENT' ? 'OPEN' : type === 'BLOCKED_BY_USER' ? 'BLOCKED' : 'FREE'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 shrink-0">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-900 rounded-xl border border-white/5"><Info size={16} className="text-blue-500"/></div>
                    <div>
                      <p className={S.unitLabel}>Configurație curentă interfață</p>
                      <p className="text-xs font-mono text-slate-400">{Object.keys(dailySlots).length} zile active în index la un pas de {intervalStep} min</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <button type="button" onClick={onClose} className="px-8 py-4 text-[10px] font-black uppercase text-slate-500 hover:text-white transition-all">Anulează</button>
                    <button type="button" onClick={handleSave} disabled={Object.keys(dailySlots).length === 0} className={`${S.confirmBtnBase} !w-64 ${Object.keys(dailySlots).length > 0 ? S.confirmBtnActive : S.confirmBtnDisabled}`}>
                      Salvează Program <Save size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExperienceCalendarModal;